const u = up.util;
const e = up.element;
up.Params = class Params {
    constructor(raw, options = {}) {
        this._options = options;
        this.clear();
        this.addAll(raw);
    }
    clear() {
        this.entries = [];
    }
    [u.copy.key]() {
        return new up.Params(this, this._options);
    }
    toObject() {
        const obj = {};
        for (let entry of this.entries) {
            const { name, value } = entry;
            if (!u.isBasicObjectProperty(name)) {
                if (this._isArrayKey(name)) {
                    obj[name] || (obj[name] = []);
                    obj[name].push(value);
                }
                else {
                    obj[name] = value;
                }
            }
        }
        return obj;
    }
    toArray() {
        return this.entries;
    }
    toFormData() {
        const formData = new FormData();
        for (let entry of this.entries) {
            formData.append(entry.name, entry.value);
        }
        if (!formData.entries) {
            formData.originalArray = this.entries;
        }
        return formData;
    }
    toQuery() {
        let simpleEntries = this.withoutBinaryEntries().entries;
        let parts = simpleEntries.map(this._arrayEntryToQuery);
        return parts.join('&');
    }
    _arrayEntryToQuery(entry) {
        const { value } = entry;
        let query = encodeURIComponent(entry.name);
        if (u.isGiven(value)) {
            query += "=";
            query += encodeURIComponent(value);
        }
        return query;
    }
    _isBinaryEntry({ value }) {
        return value instanceof Blob;
    }
    hasBinaryEntries() {
        return u.some(this.entries, this._isBinaryEntry);
    }
    withoutBinaryEntries() {
        let simpleEntries = u.reject(this.entries, this._isBinaryEntry);
        return new this.constructor(simpleEntries, this._options);
    }
    toURL(base) {
        let parts = [base, this.toQuery()];
        parts = u.filter(parts, u.isPresent);
        const separator = u.contains(base, '?') ? '&' : '?';
        return parts.join(separator);
    }
    add(name, value) {
        this.entries.push({ name, value });
    }
    addAll(raw) {
        if (u.isMissing(raw)) {
        }
        else if (raw instanceof this.constructor) {
            this.entries.push(...raw.entries);
        }
        else if (u.isArray(raw)) {
            this.entries.push(...raw);
        }
        else if (u.isString(raw)) {
            this._addAllFromQuery(raw);
        }
        else if (u.isFormData(raw)) {
            this._addAllFromFormData(raw);
        }
        else if (u.isObject(raw)) {
            this._addAllFromObject(raw);
        }
        else {
            up.fail("Unsupported params type: %o", raw);
        }
    }
    keys() {
        return u.map(this.entries, 'name');
    }
    values() {
        return u.map(this.entries, 'value');
    }
    _addAllFromObject(object) {
        for (let key in object) {
            const value = object[key];
            const valueElements = u.isArray(value) ? value : [value];
            for (let valueElement of valueElements) {
                this.add(key, valueElement);
            }
        }
    }
    _addAllFromQuery(query) {
        for (let part of query.split('&')) {
            if (part) {
                let [name, value] = part.split('=');
                name = decodeURIComponent(name);
                if (u.isGiven(value)) {
                    value = decodeURIComponent(value);
                }
                else {
                    value = null;
                }
                this.add(name, value);
            }
        }
    }
    _addAllFromFormData(formData) {
        for (let value of formData.entries()) {
            this.add(...value);
        }
    }
    set(name, value) {
        this.delete(name);
        this.add(name, value);
    }
    delete(name) {
        this.entries = u.reject(this.entries, this._matchEntryFn(name));
    }
    _matchEntryFn(name) {
        return (entry) => entry.name === name;
    }
    get(name) {
        if (this._isArrayKey(name)) {
            return this.getAll(name);
        }
        else {
            return this.getFirst(name);
        }
    }
    getFirst(name) {
        const entry = u.find(this.entries, this._matchEntryFn(name));
        return entry === null || entry === void 0 ? void 0 : entry.value;
    }
    getAll(name) {
        const entries = u.filter(this.entries, this._matchEntryFn(name));
        return u.map(entries, 'value');
    }
    _isArrayKey(key) {
        return u.evalOption(up.form.config.arrayParam, key);
    }
    [u.isBlank.key]() {
        return this.entries.length === 0;
    }
    static fromForm(form, options) {
        return this.fromContainer(form, options);
    }
    static fromContainer(container, options) {
        let fields = up.form.fields(container);
        return this.fromFields(fields, options);
    }
    static fromFields(fields, options) {
        const params = new this(null, options);
        for (let field of u.wrapList(fields)) {
            params.addField(field);
        }
        return params;
    }
    addField(field) {
        field = e.get(field);
        let name = field.name;
        if (name && this._considerFieldEnabled(field)) {
            const { tagName } = field;
            const { type } = field;
            if (tagName === 'SELECT') {
                for (let option of field.querySelectorAll('option')) {
                    if (option.selected) {
                        this.add(name, option.value);
                    }
                }
            }
            else if ((type === 'checkbox') || (type === 'radio')) {
                if (field.checked) {
                    this.add(name, field.value);
                }
            }
            else if (type === 'file') {
                for (let file of field.files) {
                    this.add(name, file);
                }
            }
            else {
                return this.add(name, field.value);
            }
        }
    }
    _considerFieldEnabled(field) {
        return !field.disabled || this._options.includeDisabled;
    }
    [u.isEqual.key](other) {
        return (this.constructor === other.constructor) && u.isEqual(this.entries, other.entries);
    }
    static fromURL(url) {
        const params = new (this)();
        const urlParts = u.parseURL(url);
        let query = urlParts.search;
        if (query) {
            query = query.replace(/^\?/, '');
            params.addAll(query);
        }
        return params;
    }
    static stripURL(url) {
        return u.normalizeURL(url, { search: false });
    }
    static merge(start, ...objects) {
        let merged = u.copy(start);
        for (let object of objects) {
            let other = u.wrapValue(this, object);
            for (let key of other.keys())
                merged.delete(key);
            merged.addAll(other);
        }
        return merged;
    }
};
